import type { FC, ReactNode } from 'react'

import { CalendarClock, HardHat } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { PROCESS_STEPS } from '@/constants/content'

export const ProcessSection: FC = (): ReactNode => {
    return (
        <section
            id='ablauf'
            aria-labelledby='ablauf-title'
            className='border-y border-border bg-card py-24 sm:py-28'
        >
            <div className='mx-auto max-w-6xl px-6'>
                <SectionHeading
                    titleId='ablauf-title'
                    eyebrow='Ablauf'
                    title='In vier Schritten zur lückenlosen Messung.'
                    description='Meist kann die Messung wenige Werktage nach dem Erstgespräch starten, idealerweise mit dem Einbau der Sensoren vor dem Betoniertermin.'
                />

                <div className='relative mt-16'>
                    <span
                        aria-hidden='true'
                        className='absolute left-6 right-0 top-6 hidden h-px bg-border lg:block'
                    />

                    <span
                        aria-hidden='true'
                        className='absolute bottom-6 left-6 top-6 w-px bg-border lg:hidden'
                    />

                    <ol className='relative grid gap-10 lg:grid-cols-4 lg:gap-6'>
                        {PROCESS_STEPS.map((step) => (
                            <li key={step.number} className='reveal relative grid grid-cols-[3rem_1fr] gap-5 lg:block'>
                                <span className='relative z-10 flex size-12 items-center justify-center rounded-full border border-border bg-card font-mono text-sm font-medium text-primary shadow-[0_0_0_6px] shadow-card'>
                                    {step.number}
                                </span>

                                <div className='lg:mt-7'>
                                    <h3 className='text-xl font-medium text-foreground'>
                                        {step.title}
                                    </h3>

                                    <p className='mt-2 text-pretty text-[0.95rem] leading-relaxed text-muted-foreground'>
                                        {step.text}
                                    </p>

                                    <div className='mt-5 rounded-2xl border border-border bg-background/60 p-4'>
                                        <p className='font-mono text-[0.64rem] font-medium uppercase tracking-[0.16em] text-muted-foreground'>
                                            Ergebnis
                                        </p>

                                        <ul className='mt-2 grid gap-1.5'>
                                            {step.deliverables.map((deliverable) => (
                                                <li key={deliverable} className='flex items-center gap-2 text-sm text-foreground'>
                                                    <span className='size-1.5 shrink-0 rounded-full bg-primary' aria-hidden='true' />
                                                    {deliverable}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>

                <div className='mt-14 grid gap-4 sm:grid-cols-2'>
                    <div className='flex gap-4 rounded-[1.5rem] border border-border bg-background/60 p-6'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-signal-soft text-signal'>
                            <CalendarClock className='size-5' aria-hidden='true' />
                        </span>

                        <div>
                            <p className='font-medium text-foreground'>
                                Betonage steht kurzfristig an?
                            </p>

                            <p className='mt-1 text-pretty text-[0.95rem] leading-relaxed text-muted-foreground'>
                                Wir priorisieren die Anreise, damit die Messung mit der ersten kritischen Betonage beginnt und keine Datenlücke entsteht.
                            </p>
                        </div>
                    </div>

                    <div className='flex gap-4 rounded-[1.5rem] border border-border bg-background/60 p-6'>
                        <span className='flex size-11 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground'>
                            <HardHat className='size-5' aria-hidden='true' />
                        </span>

                        <div>
                            <p className='font-medium text-foreground'>
                                Keine eigene Infrastruktur nötig
                            </p>

                            <p className='mt-1 text-pretty text-[0.95rem] leading-relaxed text-muted-foreground'>
                                Hardware, Installationsplan und Inbetriebnahme kommen von uns. Auf Wunsch begleiten wir die erste Betonage vor Ort.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
